import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Monitor, ShoppingCart, FileText, Package, BarChart3 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const POS_MOCKUP = 'https://static.prod-images.emergentagent.com/jobs/81285e37-432c-430a-b239-6fe7ab6d07e8/images/c10e7f68644a1fff331939ae1510bc3849ec584c4399a8172cb048aefb6f4343.png';

const DemoVideoSection = () => {
  const { t } = useLanguage();
  const [videoUrl, setVideoUrl] = useState(null);

  // Placeholder — replace VIDEO_ID with actual YouTube ID when available
  const YOUTUBE_ID = null; // e.g. 'dQw4w9WgXcQ'

  const handlePlay = () => {
    if (YOUTUBE_ID) {
      setVideoUrl(`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`);
    }
  };

  const highlights = [
    { icon: ShoppingCart, label: t('demo_hl_pos') || 'Point of Sale' },
    { icon: FileText, label: t('demo_hl_invoicing') || 'VeriFactu Invoicing' },
    { icon: Package, label: t('demo_hl_stock') || 'Stock Management' },
    { icon: BarChart3, label: t('demo_hl_reports') || 'Reports & Analytics' },
  ];

  return (
    <section className="py-20 md:py-28 bg-[#0f172a] relative overflow-hidden" data-testid="demo-video-section">
      {/* Subtle glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#22c55e]/5 rounded-full blur-[120px]" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-4"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#22c55e]/10 border border-[#22c55e]/20 text-[#22c55e] text-sm font-semibold">
            <Play className="w-3.5 h-3.5" />
            {t('demo_badge') || 'Full Demo'}
          </span>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-center mb-4"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {t('demo_title') || 'VendFactura POS System \u2014 Complete Walkthrough'}
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="text-slate-400 text-center max-w-2xl mx-auto mb-10"
        >
          {t('demo_subtitle') || 'Watch our complete walkthrough \u2014 from taking your first order to managing your stock, all in under 5 minutes.'}
        </motion.p>

        {/* Video / Placeholder */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative max-w-4xl mx-auto"
        >
          {videoUrl ? (
            /* Embedded YouTube */
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={videoUrl}
                title="VendFactura Demo"
                className="absolute inset-0 w-full h-full border-2 border-white/10"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          ) : (
            /* Placeholder with POS mockup */
            <div
              className="relative group cursor-pointer"
              onClick={handlePlay}
              data-testid="demo-video-placeholder"
            >
              {/* Thumbnail */}
              <div className="relative border-2 border-white/10 overflow-hidden bg-[#1e3a5f]">
                <img
                  src={POS_MOCKUP}
                  alt="VendFactura POS Demo"
                  className="w-full h-auto opacity-80 group-hover:opacity-90 transition-opacity"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-[#0f172a]/30 to-transparent" />

                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-[#22c55e] flex items-center justify-center shadow-lg shadow-[#22c55e]/30 group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                </div>

                {/* Bottom bar with info */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-white font-bold text-lg mb-1">
                    {t('demo_play_title') || 'VendFactura POS \u2014 Full Demo'}
                  </p>
                  <p className="text-slate-300 text-sm">
                    {YOUTUBE_ID
                      ? (t('demo_play_cta') || 'Click to play')
                      : (t('demo_coming_soon') || 'Demo video coming soon \u2014 subscribe to get notified!')}
                  </p>
                </div>
              </div>

              {/* Border glow on hover */}
              <div className="absolute -inset-px bg-gradient-to-r from-[#22c55e]/0 via-[#22c55e]/20 to-[#22c55e]/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </div>
          )}
        </motion.div>

        {/* Highlight pills */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35 }}
          className="flex flex-wrap justify-center gap-3 mt-8"
        >
          {highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white/70 text-sm">
              <h.icon className="w-4 h-4 text-[#22c55e]" />
              {h.label}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default DemoVideoSection;
